import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BasicResponsiveImage,
    BoundedContent,
    Column,
    FlexBox,
    GridContainer,
    H2,
    Row,
    TabContent,
    TabHeader,
    Tabs,
    TabsContents,
    TabsHeaders,
    Typography,
} from '@vp/swan';
import { sanitiseLocale } from '../../i18n/config';
import { useTrackNavigationClicked } from '../../utils/analytics';
import { logomakerCtaPathByLocale } from '../../utils/urls';
import LogomakerCTA from '../LogomakerCTA';
import { industryHeadings, logoExamples } from './LogoExamples';
import type { IndustryKey } from './LogoExamples';

type SpanSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

interface ColumnSpanConfig {
    span: SpanSize;
    spanMd?: SpanSize;
    spanSm?: SpanSize;
    spanXs?: SpanSize;
}

interface LogoGalleryProps {
    title?: string;
    description?: string;
    isModalLayout?: boolean;
}

const getColumnSpanConfig = (isModalLayout: boolean): ColumnSpanConfig =>
    isModalLayout
        ? {
              span: 6,
          }
        : {
              span: 2,
              spanMd: 3,
              spanSm: 4,
              spanXs: 6,
          };

const LogoGallery = ({
    title = 'Professional logos for every industry',
    description = 'No matter your industry, get a print-ready logo that looks sharp everywhere your brand shows up.',
    isModalLayout = false,
}: LogoGalleryProps) => {
    const { i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const [selectedIndustry, setSelectedIndustry] = useState<IndustryKey>(
        Object.keys(industryHeadings)[0] as IndustryKey,
    );

    const columnSpanConfig = getColumnSpanConfig(isModalLayout);

    const logomakerCTARef = useTrackNavigationClicked(
        'Design Services',
        'Logo Gallery CTA',
        logomakerCtaPathByLocale(locale),
        'logoMaker',
    );

    return (
        <BoundedContent marginY="between-sections">
            <H2 fontSkin="title-section">{title}</H2>
            <Typography fontSkin="body-standard" marginTop={4} marginBottom={6}>
                {description}
            </Typography>
            <Tabs
                id="industry-tabs"
                selectedTabId={selectedIndustry}
                showDividingLine={false}
                defaultSelectedTabId={Object.keys(industryHeadings)[0] as IndustryKey}
                onRequestTabChange={(requestedTabId) => {
                    setSelectedIndustry(requestedTabId as IndustryKey);
                }}
                aria-label="Select an industry for logo designs"
            >
                <TabsHeaders paddingBottom={6} aria-labelledby="industry-tabs">
                    {Object.entries(industryHeadings).map(([industryKey, industryHeading]) => (
                        <TabHeader key={industryKey} tabId={industryKey}>
                            {industryHeading}
                        </TabHeader>
                    ))}
                </TabsHeaders>
                <TabsContents>
                    <TabContent tabId={selectedIndustry}>
                        <GridContainer>
                            <Row as="ul">
                                {logoExamples[selectedIndustry].map(({ src, altText }, index) => (
                                    <Column
                                        as="li"
                                        key={index}
                                        {...columnSpanConfig}
                                        marginBottom={{ xs: 5, sm: 5, md: 6, lg: 6, xl: 6 }}
                                    >
                                        <BasicResponsiveImage
                                            aspectRatio={1}
                                            src={src}
                                            alt={altText}
                                        />
                                    </Column>
                                ))}
                            </Row>
                        </GridContainer>
                    </TabContent>
                </TabsContents>
            </Tabs>
            <FlexBox justifyContent="center">
                <LogomakerCTA ctaRef={logomakerCTARef} />
            </FlexBox>
        </BoundedContent>
    );
};

export default LogoGallery;
